import { z } from 'zod';
import { agentChannelSchema, agentSummarySchema } from '../agent/types';

// =============================================================================
// Member Type
// =============================================================================

export const memberTypeSchema = z.enum(['agent', 'user']);
export type MemberType = z.infer<typeof memberTypeSchema>;

// =============================================================================
// User Summary
// =============================================================================

export const userSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().optional(),
});

export type UserSummaryResponse = z.infer<typeof userSummarySchema>;

// =============================================================================
// Team Member Response
// =============================================================================

export const teamMemberSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  teamId: z.string(),
  memberType: memberTypeSchema,
  agentId: z.string().optional(),
  userId: z.string().optional(),
  selectionWeight: z.number(),
  isEscalationOnly: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  agent: agentSummarySchema.optional(),
  user: userSummarySchema.optional(),
});

export type TeamMemberResponse = z.infer<typeof teamMemberSchema>;

// =============================================================================
// Team Summary Response
// =============================================================================

export const teamSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  channel: agentChannelSchema,
  isInboundDefault: z.boolean(),
  memberCount: z.number(),
  agentCount: z.number(),
  userCount: z.number(),
});

export type TeamSummaryResponse = z.infer<typeof teamSummarySchema>;

// =============================================================================
// Team With Members Response
// =============================================================================

export const teamWithMembersSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  channel: agentChannelSchema,
  isInboundDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  members: z.array(teamMemberSchema),
  memberCount: z.number(),
  agentCount: z.number(),
  userCount: z.number(),
});

export type TeamWithMembersResponse = z.infer<typeof teamWithMembersSchema>;

// =============================================================================
// Member Requests
// =============================================================================

export const addTeamMemberRequestSchema = z.object({
  memberType: memberTypeSchema,
  agentId: z.string().uuid().optional(),
  userId: z.string().uuid().optional(),
  selectionWeight: z.number().int().min(0).max(100).optional(),
  isEscalationOnly: z.boolean().optional(),
});

export type AddTeamMemberRequest = z.infer<typeof addTeamMemberRequestSchema>;

export const updateTeamMemberRequestSchema = z.object({
  selectionWeight: z.number().int().min(0).max(100).optional(),
  isEscalationOnly: z.boolean().optional(),
});

// =============================================================================
// Create/Update Requests
// =============================================================================

export const createTeamRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  channel: agentChannelSchema,
  isInboundDefault: z.boolean().optional(),
  /**
   * Optional initial members — inserted atomically with the team in a
   * single transaction. Lets the New Team drawer stage members inline
   * and persist everything on the first Save.
   */
  members: z.array(addTeamMemberRequestSchema).max(100).optional(),
});

export type CreateTeamRequest = z.infer<typeof createTeamRequestSchema>;

export const updateTeamRequestSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  /**
   * A team's channel can change: an email team becoming the phone team that
   * takes inbound call follow-ups (ADR-352). Refused while an agent member
   * that would remain speaks another channel.
   */
  channel: agentChannelSchema.optional(),
  isInboundDefault: z.boolean().optional(),
  /**
   * Optional full replacement of the member list. When present, the server
   * diffs against current members and applies adds/updates/removes in one
   * transaction. When omitted, membership is untouched (per-member routes
   * still work for fine-grained updates).
   */
  members: z.array(addTeamMemberRequestSchema).max(100).optional(),
});

export type UpdateTeamRequest = z.infer<typeof updateTeamRequestSchema>;

export type UpdateTeamMemberRequest = z.infer<typeof updateTeamMemberRequestSchema>;
